import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Login from './Login';
import Signup from './Signup';
import { subscriptionService } from '../../services/subscription/subscriptionService';
import { wiringValue } from '../../types/subscription';

interface AuthProps {
  onAuthSuccess: () => void | Promise<void>;
}

interface LocationState {
  from?: { pathname: string };
}

const Auth: React.FC<AuthProps> = ({ onAuthSuccess }) => {
  const [view, setView] = useState<'login' | 'signup'>('login');
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const state = location.state as LocationState;

  // Get redirect path from URL params first, then location state, or default to home
  const redirectParam = searchParams.get('redirect');
  const from = redirectParam || state?.from?.pathname || '/';

  // Check for mode parameter (login/signup)
  useEffect(() => {
    const mode = searchParams.get('mode');
    if (mode === 'signup') {
      setView('signup');
    } else if (mode === 'login') {
      setView('login');
    }
  }, [searchParams]);

  const handleAuthSuccess = async (_userEmail?: string) => {
    // Where this account starts, if the server configures one (client_wiring -> landing/start_at).
    // Asked here rather than read from SubscriptionContext because that context has not loaded yet
    // at this point in the login flow — the same reason the audience count below is fetched.
    //
    // A failure falls through to the audience check, which is the route every account took before
    // this was configurable: a login must never be blocked by an optional preference.
    try {
      const planResponse = await subscriptionService.getCurrentSubscription();
      const plan = planResponse.success ? planResponse.data : undefined;
      const config = plan?.data?.clientConfig ?? plan?.clientConfig ?? null;
      const startAt = wiringValue<unknown>(config, 'landing', 'start_at', null);
      if (typeof startAt === 'string' && startAt.startsWith('/')) {
        onAuthSuccess();
        navigate(startAt, { replace: true });
        return;
      }
    } catch (error) {
      console.error('Error checking the configured landing route:', error);
    }

    // Otherwise check whether they have any audiences
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/audience/count`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        // If user has 0 audiences, redirect to build flow
        if (data.total === 0) {
          onAuthSuccess();
          navigate('/build-audience', { replace: true });
          return;
        }
      }
    } catch (error) {
      console.error('Error checking audience count:', error);
    }

    // Navigate to the page user was trying to access, or home if user already has audiences or if count check failed
    onAuthSuccess();
    navigate(from, { replace: true });
  };

  return (
    <main className="w-full max-w-md mx-auto py-12">
      {view === 'login' ? (
        <Login onSwitch={() => setView('signup')} onSuccess={handleAuthSuccess} />
      ) : (
        <Signup onSwitch={() => setView('login')} onSuccess={handleAuthSuccess} />
      )}
    </main>
  );
};

export default Auth;
